import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Recipe, RecipeDetail, recipesApi, Session, sessionsApi } from "@/client/api";
import { CookbookModal } from "@/client/components/CookbookModal";
import { GroceryItemRow } from "@/client/components/GroceryItemRow";
import { SideDrawer } from "@/client/components/SideDrawer";
import { TypeSection } from "@/client/components/TypeSection";
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorMessage,
  FormField,
  Input,
  Link,
  Modal,
  PageHeader,
  Select,
  Spinner,
  TopLoadingBar,
} from "@/client/components/ui";
import { EditableTextField } from "@/client/components/ui/EditableTextField";
import { MasterDetailLayout } from "@/client/components/ui/MasterDetailLayout";
import { NumberSpinner } from "@/client/components/ui/NumberSpinner";
import { RecipeSelect } from "@/client/components/ui/RecipeSelect";
import { buildClipboardText } from "@/client/computations/grocery";
import {
  assembleGanttString,
  buildGanttPrefixes,
  buildGanttTaskLines,
  collectResources,
} from "@/client/computations/session";
import {
  ganttCreatorUrl,
  INGREDIENT_TYPE_EMOJIS as TYPE_EMOJIS,
  INGREDIENT_TYPE_LABELS as TYPE_LABELS,
  INGREDIENT_TYPE_ORDER as TYPE_ORDER,
} from "@/client/constants";
import { useDataVersion } from "@/client/context/DataVersionContext";
import { useToast } from "@/client/context/ToastContext";
import { useApi } from "@/client/hooks/useApi";
import { useCyclingIndex } from "@/client/hooks/useCyclingIndex";
import { useGroceryChecks } from "@/client/hooks/useGroceryChecks";
import {
  AddIcon,
  ChecklistIcon,
  CompleteIcon,
  CopyIcon,
  DeleteIcon,
  ExternalLinkIcon,
  FreezerIcon,
  GroceryIcon,
  RecipeIcon,
  SessionIcon,
  StockIcon,
} from "@/client/icons";
import { toDateStr } from "@/shared/date";
import { APP_LOCALE } from "@/shared/locale";

// ─── Add batch form ────────────────────────────────────────────────────────────

function AddBatchModal({
  sessionId,
  sessionDate,
  recipes,
  onDone,
  onClose,
}: {
  sessionId: string;
  sessionDate: string;
  recipes: Recipe[];
  onDone: () => void;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [recipeId, setRecipeId] = useState("");
  const [servings, setServings] = useState("1");
  const [date, setDate] = useState(sessionDate);
  const [storageLocation, setStorageLocation] = useState<"fridge" | "freezer">("freezer");
  const [variantId, setVariantId] = useState<string | null>(null);
  const [variants, setVariants] = useState<{ id: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);

  // Fetch variants when recipe changes
  /* eslint-disable react-hooks/set-state-in-effect -- syncing variant list from async fetch */
  useEffect(() => {
    setVariantId(null);
    setVariants([]);
    if (!recipeId) return;
    let stale = false;
    recipesApi
      .get(recipeId)
      .then((detail) => {
        if (!stale) setVariants(detail.variants ?? []);
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [recipeId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  async function handleSubmit() {
    if (!recipeId || !servings || !date) return;
    // Warn if recipe has variants but none selected
    if (variants.length > 0 && !variantId) {
      toast("Seleccioná una variante para este lote", "error");
      return;
    }
    setSaving(true);
    try {
      await sessionsApi.addBatch(sessionId, {
        recipe_id: recipeId,
        servings_produced: parseFloat(servings),
        prep_date: date,
        storage_location: storageLocation,
        variant_id: variantId,
      });
      toast("Lote agregado");
      onDone();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Agregar lote"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving || !recipeId}>
            {saving ? "Guardando..." : "Agregar"}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <FormField label="Receta">
          <RecipeSelect
            recipes={recipes}
            value={recipeId}
            onChange={setRecipeId}
            placeholder="Buscar receta..."
            filterFn={(r) => r.has_meal_prep_steps === 1}
          />
        </FormField>
        {variants.length > 0 && (
          <FormField label="Variante">
            <Select
              value={variantId ?? ""}
              onChange={(v) => setVariantId(v || null)}
              options={variants.map((v) => ({ value: v.id, label: v.name }))}
              placeholder="Elegir variante..."
            />
          </FormField>
        )}
        <FormField label="Porciones producidas">
          <Input value={servings} onChange={setServings} type="number" placeholder="4" />
        </FormField>
        <FormField label="Fecha de preparación">
          <Input value={date} onChange={setDate} type="date" />
        </FormField>
        <FormField label="Almacenamiento">
          <Select
            value={storageLocation}
            onChange={(v) => setStorageLocation(v as "fridge" | "freezer")}
            options={[
              { value: "freezer", label: "Freezer" },
              { value: "fridge", label: "Heladera" },
            ]}
          />
        </FormField>
      </form>
    </Modal>
  );
}

// ─── Grocery list modal ───────────────────────────────────────────────────────

function GroceryListModal({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const { toast } = useToast();
  const [phase, setPhase] = useState<"meal-prep" | "day-of-eating" | "all">("meal-prep");
  const [repIndex, cycleRep] = useCyclingIndex();
  const checksApi = useMemo(
    () => ({
      list: () => sessionsApi.groceryChecks(sessionId),
      set: (key: string, checked: boolean) => sessionsApi.setGroceryCheck(sessionId, key, checked),
    }),
    [sessionId],
  );
  const [checked, toggleChecked] = useGroceryChecks(checksApi);

  const fetcher = useCallback(() => sessionsApi.groceryList(sessionId, phase), [sessionId, phase]);
  const { data, loading, error } = useApi(fetcher, [sessionId, phase]);

  async function copyToClipboard() {
    if (!data) return;
    const text = buildClipboardText(data.items, repIndex, TYPE_ORDER, TYPE_EMOJIS, TYPE_LABELS);
    await navigator.clipboard.writeText(text);
    toast("Lista copiada al portapapeles");
  }

  const hasItems = data && Object.values(data.items).some((items) => items.length > 0);

  return (
    <SideDrawer
      title="Lista de compras"
      icon={
        <GroceryIcon width={20} height={20} className="text-term-accent shrink-0" aria-hidden />
      }
      onClose={onClose}
      toolbar={
        <>
          <div className="flex items-center gap-1">
            {[
              { key: "meal-prep" as const, label: "Meal prep" },
              { key: "day-of-eating" as const, label: "Día de" },
              { key: "all" as const, label: "Todos" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setPhase(key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all ${
                  phase === key
                    ? "bg-term-accent text-white"
                    : "text-term-text-muted hover:text-term-text hover:bg-term-raised"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {!loading && hasItems && (
            <button
              onClick={() => void copyToClipboard()}
              className="text-sm text-term-text-muted hover:text-term-accent cursor-pointer transition-colors flex items-center gap-1"
              type="button"
            >
              <CopyIcon width={14} height={14} className="shrink-0" aria-hidden />
              Copiar
            </button>
          )}
        </>
      }
    >
      {loading && <Spinner className="!py-6" />}
      {!loading && error && <p className="text-sm text-term-error">{error}</p>}

      {!loading && data && !hasItems && (
        <p className="text-sm text-term-text-muted text-center py-6">
          Sin ingredientes para esta fase.
        </p>
      )}

      {!loading && data && hasItems && (
        <div className="flex flex-col">
          {TYPE_ORDER.filter((t) => data.items[t]?.length).map((type) => (
            <TypeSection key={type} emoji={TYPE_EMOJIS[type]!} label={TYPE_LABELS[type] ?? type}>
              <div className="flex flex-col">
                {data.items[type]!.map((item, i) => (
                  <GroceryItemRow
                    key={i}
                    item={item}
                    repIndex={repIndex[item.ingredient_id] ?? 0}
                    isChecked={checked.has(item.ingredient_id)}
                    onToggle={() => toggleChecked(item.ingredient_id)}
                    onCycleRep={() => cycleRep(item.ingredient_id, item.representations.length)}
                  />
                ))}
              </div>
            </TypeSection>
          ))}
        </div>
      )}
    </SideDrawer>
  );
}

// ─── Session detail ────────────────────────────────────────────────────────────

function SessionDetail({
  session,
  recipes,
  onDetailChanged,
  onListChanged,
  onDeleted,
}: {
  session: Session;
  recipes: Recipe[];
  /** Refresh only the detail panel (batch edits, notes) */
  onDetailChanged: () => void;
  /** Refresh both list and detail (status change, add/remove batch) */
  onListChanged: () => void;
  onDeleted: () => void;
}) {
  const { toast } = useToast();
  const [showAddBatch, setShowAddBatch] = useState(false);
  const [showGroceryDrawer, setShowGroceryDrawer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<"batch" | "session" | null>(null);
  const [pendingBatchId, setPendingBatchId] = useState<string | null>(null);
  const [cookbooks, setCookbooks] = useState<string[] | null>(null);
  const [loadingCookbooks, setLoadingCookbooks] = useState(false);

  async function saveNotes(value: string) {
    setSaving(true);
    try {
      await sessionsApi.update(session.id, { notes: value });
      toast("Notas guardadas");
      onDetailChanged();
    } finally {
      setSaving(false);
    }
  }

  async function saveGantt(value: string) {
    setSaving(true);
    try {
      await sessionsApi.update(session.id, { gantt: value || null });
      toast("Gantt guardado");
      onDetailChanged();
    } finally {
      setSaving(false);
    }
  }

  function openGanttEditor() {
    window.open(ganttCreatorUrl(session.gantt ?? undefined), "_blank");
  }

  async function createBaseGantt() {
    if (!session.batches?.length) return;
    const recipeIds = [...new Set(session.batches.map((b) => b.recipe_id))];
    const details = await Promise.all(recipeIds.map((id) => recipesApi.get(id)));
    const detailMap = new Map<string, RecipeDetail>(details.map((d) => [d.id, d]));

    const prefixes = buildGanttPrefixes(recipeIds, detailMap);
    const resources = collectResources(details);
    const taskLines = buildGanttTaskLines(recipeIds, detailMap, prefixes);
    const ganttStr = assembleGanttString(resources, taskLines);

    window.open(ganttCreatorUrl(ganttStr), "_blank");
  }

  async function openCookbooks() {
    setLoadingCookbooks(true);
    try {
      const { cookbooks: data } = await sessionsApi.cookbooks(session.id);
      setCookbooks(data);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error generando cookbooks", "error");
    } finally {
      setLoadingCookbooks(false);
    }
  }

  function copyGantt() {
    void navigator.clipboard.writeText(session.gantt ?? "");
    toast("Gantt copiado al portapapeles");
  }

  function promptDeleteBatch(batchId: string) {
    setPendingBatchId(batchId);
    setConfirmDelete("batch");
  }

  async function confirmDeleteBatch() {
    if (!pendingBatchId) return;
    await sessionsApi.deleteBatch(session.id, pendingBatchId);
    toast("Lote eliminado");
    setConfirmDelete(null);
    setPendingBatchId(null);
    onListChanged();
  }

  async function toggleStorage(batchId: string, current: "fridge" | "freezer") {
    const next = current === "freezer" ? "fridge" : "freezer";
    await sessionsApi.updateBatch(session.id, batchId, { storage_location: next });
    toast(next === "freezer" ? "Movido al freezer" : "Movido a la heladera");
    onDetailChanged();
  }

  async function updateBatchServings(batchId: string, newServings: number) {
    if (newServings < 1) return;
    await sessionsApi.updateBatch(session.id, batchId, { servings_produced: newServings });
    onDetailChanged();
  }

  async function confirmDeleteSession() {
    await sessionsApi.delete(session.id);
    toast("Sesión eliminada");
    setConfirmDelete(null);
    onDeleted();
  }

  const batches = session.batches ?? [];

  async function toggleStatus() {
    const newStatus = session.status === "planned" ? "completed" : "planned";
    await sessionsApi.update(session.id, { status: newStatus });
    toast(
      newStatus === "completed"
        ? "Sesión marcada como completada"
        : "Sesión marcada como planificada",
    );
    onListChanged();
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-lg text-term-text">
          {new Date(session.date + "T00:00:00").toLocaleDateString(APP_LOCALE, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </h2>
        {batches.length > 0 && (
          <Button variant="secondary" size="sm" onClick={() => setShowGroceryDrawer(true)}>
            <GroceryIcon width={14} height={14} />
            Ingredientes
          </Button>
        )}
      </div>

      {/* Status */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => void toggleStatus()}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition-all border ${
            session.status === "planned"
              ? "bg-term-accent/10 border-term-accent/30 text-term-accent"
              : "bg-term-user/10 border-term-user/30 text-term-user"
          }`}
        >
          {session.status === "planned" ? (
            <>
              <ChecklistIcon width={14} height={14} className="shrink-0" aria-hidden />
              Planificada
            </>
          ) : (
            <>
              <CompleteIcon width={14} height={14} className="shrink-0" aria-hidden />
              Completada
            </>
          )}
        </button>
        <span className="text-micro text-term-text-muted">
          {session.status === "planned"
            ? "Los lotes no cuentan como stock"
            : "Click para marcar como planificada"}
        </span>
      </div>

      {/* Notes */}
      <EditableTextField
        label="Notas"
        value={session.notes ?? ""}
        onSave={saveNotes}
        saving={saving}
        placeholder="Notas..."
        emptyText="Sin notas"
      />

      {/* Gantt */}
      <EditableTextField
        label="Gantt"
        value={session.gantt ?? ""}
        onSave={saveGantt}
        saving={saving}
        placeholder={
          "VERSION 1\n\nSTART 09:00\nEND 14:00\n\nLANES\nPersona: 2\nHorno\n\nTASKS\n..."
        }
        rows={8}
        mono
        emptyText="Sin diagrama Gantt"
        headerExtra={
          !session.gantt && session.batches && session.batches.length > 0 ? (
            <button
              onClick={() => void createBaseGantt()}
              className="text-sm text-term-accent cursor-pointer hover:underline flex items-center gap-0.5"
            >
              <ExternalLinkIcon width={10} height={10} />
              Crear en editor
            </button>
          ) : undefined
        }
        viewContent={
          session.gantt ? (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={copyGantt}>
                <CopyIcon width={13} height={13} />
                Copiar
              </Button>
              <Button variant="secondary" size="sm" onClick={openGanttEditor}>
                <ExternalLinkIcon width={13} height={13} />
                Ver en editor
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void openCookbooks()}
                disabled={loadingCookbooks}
              >
                <RecipeIcon width={13} height={13} />
                {loadingCookbooks ? "Generando..." : "Cookbooks"}
              </Button>
            </div>
          ) : undefined
        }
      />

      {/* Batches */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-term-text-dim uppercase tracking-wide">
            Lotes ({batches.length})
          </span>
          <Button variant="secondary" size="sm" onClick={() => setShowAddBatch(true)}>
            <AddIcon width={12} height={12} />
            Agregar lote
          </Button>
        </div>

        {batches.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <p className="text-sm text-term-text-muted">Sin lotes registrados.</p>
            <Button variant="primary" size="sm" onClick={() => setShowAddBatch(true)}>
              <AddIcon width={12} height={12} />
              Agregar lote
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {batches.map((b) => (
              <div
                key={b.id}
                className="py-2.5 px-3 rounded-lg border border-transparent hover:bg-term-raised/50 hover:border-term-border transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <Link
                    href={`/recetas/${b.recipe_id}?porciones=${b.servings_produced}${b.variant_id ? `&variante=${b.variant_id}` : ""}`}
                    className="flex-1 min-w-0 text-left"
                  >
                    <span className="text-sm font-medium text-term-text group-hover:text-term-accent transition-colors">
                      {b.recipe_name}
                    </span>
                    {b.recipe_type && <Badge value={b.recipe_type} className="ml-2" />}
                    {b.variant_name && (
                      <span className="ml-1.5 text-micro font-medium text-term-accent bg-term-accent/10 px-1.5 py-0.5 rounded-full border border-term-accent/20">
                        {b.variant_name}
                      </span>
                    )}
                  </Link>
                  {/* Desktop-only inline controls */}
                  <div className="hidden md:flex items-center gap-3 text-sm text-term-text-muted shrink-0">
                    <button
                      onClick={() => void toggleStorage(b.id, b.storage_location)}
                      className="cursor-pointer hover:opacity-70 focus-visible:opacity-70 transition-opacity inline-flex items-center justify-center"
                      title={
                        b.storage_location === "freezer"
                          ? "En freezer — click para cambiar"
                          : "En heladera — click para cambiar"
                      }
                      type="button"
                    >
                      {b.storage_location === "freezer" ? (
                        <FreezerIcon
                          width={15}
                          height={15}
                          className="text-term-text-muted"
                          aria-hidden
                        />
                      ) : (
                        <StockIcon
                          width={15}
                          height={15}
                          className="text-term-text-muted"
                          aria-hidden
                        />
                      )}
                    </button>
                    <span className="inline-flex items-center gap-1.5">
                      <NumberSpinner
                        value={b.servings_produced}
                        onDecrement={() => void updateBatchServings(b.id, b.servings_produced - 1)}
                        onIncrement={() => void updateBatchServings(b.id, b.servings_produced + 1)}
                      />
                      <span>porc.</span>
                    </span>
                    <span>{b.prep_date}</span>
                    <button
                      onClick={() => promptDeleteBatch(b.id)}
                      className="text-term-text-muted md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 hover:text-term-error cursor-pointer transition-all"
                      aria-label="Borrar lote"
                    >
                      <DeleteIcon width={14} height={14} />
                    </button>
                  </div>
                </div>
                {/* Mobile-only stacked controls */}
                <div className="flex md:hidden items-center gap-3 mt-1.5 text-sm text-term-text-muted">
                  <button
                    onClick={() => void toggleStorage(b.id, b.storage_location)}
                    className="cursor-pointer hover:opacity-70 transition-opacity inline-flex items-center justify-center"
                    title={
                      b.storage_location === "freezer"
                        ? "En freezer — click para cambiar"
                        : "En heladera — click para cambiar"
                    }
                    type="button"
                  >
                    {b.storage_location === "freezer" ? (
                      <FreezerIcon
                        width={15}
                        height={15}
                        className="text-term-text-muted"
                        aria-hidden
                      />
                    ) : (
                      <StockIcon
                        width={15}
                        height={15}
                        className="text-term-text-muted"
                        aria-hidden
                      />
                    )}
                  </button>
                  <span className="inline-flex items-center gap-1.5">
                    <NumberSpinner
                      value={b.servings_produced}
                      onDecrement={() => void updateBatchServings(b.id, b.servings_produced - 1)}
                      onIncrement={() => void updateBatchServings(b.id, b.servings_produced + 1)}
                    />
                    <span>porc.</span>
                  </span>
                  <span>{b.prep_date}</span>
                  <button
                    onClick={() => promptDeleteBatch(b.id)}
                    className="text-term-text-muted hover:text-term-error cursor-pointer transition-all ml-auto"
                    aria-label="Borrar lote"
                  >
                    <DeleteIcon width={14} height={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-term-border">
        <Button variant="danger" size="sm" onClick={() => setConfirmDelete("session")}>
          <DeleteIcon width={13} height={13} />
          Borrar sesión
        </Button>
      </div>

      {cookbooks && <CookbookModal cookbooks={cookbooks} onClose={() => setCookbooks(null)} />}

      {showGroceryDrawer && (
        <GroceryListModal sessionId={session.id} onClose={() => setShowGroceryDrawer(false)} />
      )}

      {showAddBatch && (
        <AddBatchModal
          sessionId={session.id}
          sessionDate={session.date}
          recipes={recipes}
          onDone={() => {
            setShowAddBatch(false);
            onListChanged();
          }}
          onClose={() => setShowAddBatch(false)}
        />
      )}

      {confirmDelete === "batch" && (
        <ConfirmModal
          message="¿Borrar este lote? Se eliminarán también sus registros de consumo."
          confirmLabel="Borrar"
          danger
          onConfirm={() => void confirmDeleteBatch()}
          onCancel={() => {
            setConfirmDelete(null);
            setPendingBatchId(null);
          }}
        />
      )}
      {confirmDelete === "session" && (
        <ConfirmModal
          message="¿Borrar esta sesión y todos sus lotes? Esta acción no se puede deshacer."
          confirmLabel="Borrar sesión"
          danger
          onConfirm={() => void confirmDeleteSession()}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SessionsPage({ initialSessionId }: { initialSessionId?: string } = {}) {
  const { dataVersion } = useDataVersion();
  const [selectedId, setSelectedId] = useState<string | null>(initialSessionId ?? null);
  const [showNew, setShowNew] = useState(false);
  const [newDate, setNewDate] = useState(toDateStr(new Date()));
  const [newNotes, setNewNotes] = useState("");
  const [newStatus, setNewStatus] = useState<"completed" | "planned">("planned");
  const [creating, setCreating] = useState(false);

  const sessionsFetcher = useCallback(() => {
    void dataVersion;
    return sessionsApi.list();
  }, [dataVersion]);
  const recipesFetcher = useCallback(() => recipesApi.list(), []);
  const detailFetcher = useCallback(() => {
    void dataVersion;
    return selectedId ? sessionsApi.get(selectedId) : Promise.resolve(null);
  }, [selectedId, dataVersion]);

  const { data: sessions, loading, error, refetch } = useApi(sessionsFetcher, [dataVersion]);
  const { data: recipes } = useApi(recipesFetcher, []);
  const {
    data: detail,
    loading: detailLoading,
    refetch: refetchDetail,
  } = useApi(detailFetcher as () => Promise<Session | null>, [selectedId]);

  function selectSession(id: string | null) {
    setSelectedId(id);
    const url = id ? `/sesiones/${id}` : "/sesiones";
    window.history.replaceState(null, "", url);
  }

  async function createSession() {
    if (!newDate) return;
    setCreating(true);
    try {
      const { id } = await sessionsApi.create({
        date: newDate,
        notes: newNotes || undefined,
        status: newStatus,
      });
      setShowNew(false);
      setNewNotes("");
      await refetch();
      selectSession(id);
    } finally {
      setCreating(false);
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (
        !sessions ||
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      )
        return;
      const curIdx = sessions.findIndex((s) => s.id === selectedId);
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        const next = curIdx < sessions.length - 1 ? curIdx + 1 : 0;
        selectSession(sessions[next]!.id);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        const next = curIdx > 0 ? curIdx - 1 : sessions.length - 1;
        selectSession(sessions[next]!.id);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sessions, selectedId]);

  if (loading) return <TopLoadingBar visible />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!sessions) return null;

  const selected = selectedId ? sessions.find((s) => s.id === selectedId) : null;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto flex flex-col gap-6">
      <TopLoadingBar visible={detailLoading} />
      <PageHeader
        title="Sesiones"
        icon={<SessionIcon width={22} height={22} />}
        subtitle="Historial de sesiones de meal prep"
        actions={
          <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>
            <AddIcon width={14} height={14} />
            Nueva sesión
          </Button>
        }
      />

      {sessions.length === 0 && !showNew ? (
        <EmptyState
          icon={<SessionIcon strokeWidth={1.25} />}
          title="Sin sesiones"
          description="Registrá tu primera sesión de meal prep."
          action={
            <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>
              <AddIcon width={14} height={14} />
              Nueva sesión
            </Button>
          }
        />
      ) : (
        <MasterDetailLayout
          hasSelection={!!selected}
          detailLoading={detailLoading}
          onBack={() => selectSession(null)}
          backLabel="Sesiones"
          emptyMessage="Seleccioná una sesión para ver los detalles"
          list={
            <div className="flex flex-col gap-2">
              {showNew && (
                <Card className="p-4 flex flex-col gap-3 border-term-accent/40">
                  <FormField label="Fecha">
                    <Input value={newDate} onChange={setNewDate} type="date" />
                  </FormField>
                  <FormField label="Notas (opcional)">
                    <Input value={newNotes} onChange={setNewNotes} placeholder="Notas..." />
                  </FormField>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setNewStatus((s) => (s === "completed" ? "planned" : "completed"))
                      }
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition-all border ${
                        newStatus === "planned"
                          ? "bg-term-accent/10 border-term-accent/30 text-term-accent"
                          : "bg-term-user/10 border-term-user/30 text-term-user"
                      }`}
                    >
                      {newStatus === "planned" ? (
                        <>
                          <ChecklistIcon width={14} height={14} className="shrink-0" aria-hidden />
                          Planificada
                        </>
                      ) : (
                        <>
                          <CompleteIcon width={14} height={14} className="shrink-0" aria-hidden />
                          Completada
                        </>
                      )}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="primary" size="sm" onClick={createSession} disabled={creating}>
                      {creating ? "Creando..." : "Crear"}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowNew(false)}>
                      Cancelar
                    </Button>
                  </div>
                </Card>
              )}

              {sessions.map((s, i) => (
                <div
                  key={s.id}
                  ref={
                    s.id === selectedId
                      ? (el) => el?.scrollIntoView({ block: "nearest" })
                      : undefined
                  }
                >
                  <Card
                    index={i}
                    onClick={() => selectSession(s.id)}
                    className={`p-3.5 ${selectedId === s.id ? "border-l-2 border-l-term-accent border-term-accent/30 bg-term-accent/5" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-term-text">
                        {new Date(s.date + "T00:00:00").toLocaleDateString(APP_LOCALE, {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                      {s.status === "planned" && (
                        <span className="text-micro font-medium text-term-accent bg-term-accent/10 px-1.5 py-0.5 rounded">
                          planificada
                        </span>
                      )}
                      {s.gantt && (
                        <span className="text-micro font-medium text-term-text-muted bg-term-raised px-1.5 py-0.5 rounded">
                          gantt
                        </span>
                      )}
                    </div>
                    {s.notes && (
                      <div className="text-sm text-term-text-muted mt-0.5 line-clamp-1">
                        {s.notes}
                      </div>
                    )}
                    <div className="text-sm text-term-text-muted mt-1">
                      {s.batch_count} lote{s.batch_count !== 1 ? "s" : ""} · {s.total_servings}{" "}
                      porciones
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          }
          detail={
            selected && detail ? (
              <Card className="p-5">
                <SessionDetail
                  key={detail.id}
                  session={detail}
                  recipes={recipes ?? []}
                  onDetailChanged={() => void refetchDetail()}
                  onListChanged={() => {
                    void refetch();
                    void refetchDetail();
                  }}
                  onDeleted={() => {
                    selectSession(null);
                    void refetch();
                  }}
                />
              </Card>
            ) : null
          }
        />
      )}
    </div>
  );
}
